import {
    CURRENT_FUTURE,
    DATES_MAPPING,
} from '@eventbrite/consumer-search.discover-utils/constants';
import { defineMessages, MessageDescriptor } from 'react-intl';
import { BEST_OF_CITY_EVENTS, LIKED_EVENTS, PRIMARY_BUCKET, SECONDARY_BUCKET } from '@/src/homepage/constants';

export const ALL_TAB = 'all';
export const THIS_WEEKEND_TAB = 'this_weekend';
export const FOR_YOU = 'for_you';
export const TODAY_TAB = 'today';

export const FEED_TABS = [ALL_TAB, FOR_YOU, TODAY_TAB, THIS_WEEKEND_TAB];

const messages = defineMessages({
    all: {
        id: 'All',
        defaultMessage: 'All',
    },
    editorsPicks: {
        id: "Editor's picks",
        defaultMessage: "Editor's picks",
    },
    editorsPicksSubtitle: {
        id: "Discover {location}'s best experiences, hand-picked by our experts.",
        defaultMessage: "Discover {location}'s best experiences, hand-picked by our experts.",
    },
    eventsInLocation: {
        id: 'Events in {location}',
        defaultMessage: 'Events in {location}',
    },
    moreEvents: {
        id: 'More events',
        defaultMessage: 'More events',
    },
    forYou: {
        id: 'For you',
        defaultMessage: 'For you',
    },
    myLikedEvents: {
        id: 'My liked events',
        defaultMessage: 'My liked events',
    },
    likeFavorites: {
        id: 'Like your favorites events',
        defaultMessage: 'Like your favorites events',
    },
    betterRecommendations: {
        id: 'Get better recomendations and see more events you´ll love',
        defaultMessage: 'Get better recomendations and see more events you´ll love',
    },
    moreEventsWeekend: {
        id: 'More events this weekend',
        defaultMessage: 'More events this weekend',
    },
    moreEventsToday: {
        id: 'More events today',
        defaultMessage: 'More events today',
    },
});

type EmptyStateBucketValidation = {
    shouldShowEmptyState: boolean;
    isAuthenticated: boolean;
    useDefault?: boolean;
};

type EmptyStateBucketConfig = {
    title: MessageDescriptor;
    iconType: string;
    iconSize: string;
    description: MessageDescriptor;
};

type EmptyStateBucket = {
    validation: EmptyStateBucketValidation;
    config?: EmptyStateBucketConfig;
};

type BucketConfig = {
    order: number;
    propsOverwrite: {
        name?: MessageDescriptor;
        subtitle?: MessageDescriptor;
    };
    emptyState: EmptyStateBucket[];
};

type EventSearch = {
    dates: string[];
    dedup?: boolean;
};

export type TabConfig = {
    tabKeyLabel: string;
    name: MessageDescriptor;
    eventSearch?: EventSearch;
    follow?: string[];
    promotedSlots: number;
    feedConfig: Record<string, BucketConfig>;
};

export const TABS_CONFIG: Record<string, TabConfig> = {
    [ALL_TAB]: {
        tabKeyLabel: 'All',
        name: messages.all,
        eventSearch: {
            dates: [CURRENT_FUTURE],
            dedup: true,
        },
        promotedSlots: 3,
        feedConfig: {
            [BEST_OF_CITY_EVENTS]: {
                order: 1,
                propsOverwrite: {
                    name: messages.editorsPicks,
                    subtitle: messages.editorsPicksSubtitle,
                },
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: false,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: true,
                        },
                    },
                ],
            },
            [PRIMARY_BUCKET]: {
                order: 2,
                propsOverwrite: {
                    name: messages.eventsInLocation,
                },
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: true,
                            useDefault: true,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: false,
                            useDefault: true,
                        },
                    },
                ],
            },
            [SECONDARY_BUCKET]: {
                order: 5,
                propsOverwrite: {
                    name: messages.moreEvents,
                },
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: true,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: false,
                        },
                    },
                ],
            },
        },
    },
    [FOR_YOU]: {
        tabKeyLabel: FOR_YOU,
        name: messages.forYou,
        promotedSlots: 3,
        feedConfig: {
            [LIKED_EVENTS]: {
                order: 2,
                propsOverwrite: {
                    name: messages.myLikedEvents,
                },
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: true,
                        },
                        config: {
                            title: messages.likeFavorites,
                            iconType: 'Heart',
                            iconSize: 'medium',
                            description: messages.betterRecommendations,
                        },
                    },
                ],
            },
        },
    },
    [THIS_WEEKEND_TAB]: {
        tabKeyLabel: 'ThisWeekend',
        name: DATES_MAPPING[THIS_WEEKEND_TAB],
        eventSearch: {
            dates: [CURRENT_FUTURE, 'this_weekend'],
        },
        follow: [],
        promotedSlots: 3,
        feedConfig: {
            [PRIMARY_BUCKET]: {
                order: 1,
                propsOverwrite: {},
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: true,
                            useDefault: true,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: false,
                            useDefault: true,
                        },
                    },
                ],
            },
            [SECONDARY_BUCKET]: {
                order: 3,
                propsOverwrite: {
                    name: messages.moreEventsWeekend,
                },
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: true,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: false,
                        },
                    },
                ],
            },
        },
    },
    [TODAY_TAB]: {
        tabKeyLabel: 'Today',
        name: DATES_MAPPING[TODAY_TAB],
        eventSearch: {
            dates: [CURRENT_FUTURE, 'today'],
        },
        follow: [],
        promotedSlots: 3,
        feedConfig: {
            [PRIMARY_BUCKET]: {
                order: 1,
                propsOverwrite: {},
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: true,
                            useDefault: true,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: true,
                            isAuthenticated: false,
                            useDefault: true,
                        },
                    },
                ],
            },
            [SECONDARY_BUCKET]: {
                order: 2,
                propsOverwrite: {
                    name: messages.moreEventsToday,
                },
                emptyState: [
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: true,
                        },
                    },
                    {
                        validation: {
                            shouldShowEmptyState: false,
                            isAuthenticated: false,
                        },
                    },
                ],
            },
        },
    },
};
